/**
 * BondedSkillEffect — Conjuration Bind path Chimera passive: familiar aggro parity.
 *
 * Always-on passive. The companion familiar generates aggro equal to the
 * player's own — enemies split their attention rather than dismissing the
 * familiar as a secondary distraction.
 *
 * The JSON value `aggroGenerationMultiplier: 1` encodes "1× player aggro
 * weight" as a direct override, not an additive bonus on top of the
 * companion's default 0.3. When Bonded is active, the integration layer
 * calls `getCompanionAggroWeight(playerBaseAggroWeight)` instead of using
 * the companion's intrinsic aggroWeight.
 *
 * Resolver stacking: future upgrades in the 'chimera' scope can raise the
 * multiplier above 1.0 (familiar draws MORE than the player) via the
 * `aggroGenerationMultiplier` key.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Bonded
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BondedDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface BondedConfig {
  /**
   * Base multiplier applied to the player's aggro weight to derive the
   * companion's aggro weight. 1.0 = full parity (50/50 split with two
   * entities in the threat pool).
   */
  readonly aggroGenerationMultiplier: number;
}

const DEFAULT_CONFIG: BondedConfig = {
  aggroGenerationMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BondedSkillEffect implements SkillEffect {
  readonly skillId = 'chimera_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BondedDeps;
  private readonly config: BondedConfig;

  // Observable state for simulation scenarios
  private _queriesCount = 0;

  constructor(deps: BondedDeps, config: Partial<BondedConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Bonded-specific API ---

  /**
   * Compute the companion's aggro weight as a function of the player's base.
   *
   * With the default multiplier of 1.0 and no resolver bonuses, the familiar
   * gets the same aggro weight as the player — a two-entity threat pool
   * where enemies split attention 50/50. A multiplier above 1.0 makes the
   * familiar draw proportionally more aggro than the player.
   *
   * The resolved multiplier = config base + resolver additive bonuses.
   * Clamped to a floor of 0 (familiar cannot have negative aggro).
   *
   * @param playerBaseAggroWeight The player's current base aggro weight
   */
  getCompanionAggroWeight(playerBaseAggroWeight: number): number {
    const override = this.deps.resolver.getMaxEffect(
      'chimera', 'aggroGenerationMultiplier',
    );
    const resolvedMultiplier = Math.max(0, override ?? this.config.aggroGenerationMultiplier);
    this._queriesCount++;
    return playerBaseAggroWeight * resolvedMultiplier;
  }

  /**
   * Compute the fraction of enemy target selections that should land on
   * the companion, given a two-entity threat pool (player + familiar).
   *
   * With parity (multiplier 1.0): returns 0.5 (50% of enemies target familiar).
   * Returns 0 if the resulting companion weight is 0.
   *
   * @param playerBaseAggroWeight The player's current base aggro weight
   */
  getCompanionAggroFraction(playerBaseAggroWeight: number): number {
    const companionWeight = this.getCompanionAggroWeight(playerBaseAggroWeight);
    // Undo the queriesCount increment from the helper call — the fraction
    // query is conceptually one query, not two.
    this._queriesCount--;
    const total = playerBaseAggroWeight + companionWeight;
    if (total <= 0) return 0;
    this._queriesCount++;
    return companionWeight / total;
  }

  // --- State getters ---

  get queriesCount(): number { return this._queriesCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — aggro weight is computed on demand
  }

  destroy(): void {
    // No resources to release
  }
}
